import { useState, useEffect, useRef } from "react";
import type { SearchResult } from "@/lib/data/stock-search";

const DEBOUNCE_MS = 300;

export function useSearch(query: string, exchange?: string) {
  const [results, setResults] = useState<SearchResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    // Clear immediately when query is too short
    if (query.length < 2) {
      setResults([]);
      setIsLoading(false);
      setError(null);
      return;
    }

    setIsLoading(true);
    setError(null);

    const timer = setTimeout(async () => {
      // Abort any in-flight request
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      try {
        const params = new URLSearchParams({ q: query });
        if (exchange) params.set("exchange", exchange);

        const res = await fetch(`/api/stocks/search?${params}`, {
          signal: controller.signal,
        });

        if (!res.ok) throw new Error("Search request failed");

        const json = await res.json();

        if (!json.success) throw new Error(json.error ?? "Search failed");

        setResults(json.data);
        setError(null);
      } catch (err: unknown) {
        if (err instanceof DOMException && err.name === "AbortError") return;
        setError(err instanceof Error ? err.message : "Search failed");
        setResults([]);
      } finally {
        if (!controller.signal.aborted) {
          setIsLoading(false);
        }
      }
    }, DEBOUNCE_MS);

    return () => {
      clearTimeout(timer);
      abortRef.current?.abort();
    };
  }, [query, exchange]);

  return { results, isLoading, error };
}
